import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Trash2, Boxes, AlertTriangle, Wallet, Store, Warehouse, MapPin,
  ShieldCheck, ClipboardCheck, ShoppingCart, Bell, CalendarClock,
  Gauge, Percent, PackageCheck, Award, Activity,
  Building2, Star, ShoppingBag, BadgeCheck, MessageCircle, Plus, Layers, ArrowUpRight, ArrowDownRight,
  Coins, Truck,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, Legend,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "../lib/api";
import { Card, PageLoader, Badge, Button, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { BranchBadge, ProductAvatar } from "../components/visual";
import CostInsights from "../components/CostInsights";
import ProductionInsights from "../components/ProductionInsights";
import MarginInsights from "../components/MarginInsights";
import EmployeeMealWidget from "../components/EmployeeMealWidget";
import ClosingStatusWidget from "../components/ClosingStatusWidget";
import ControlTower from "../components/ControlTower";
import OnboardingChecklist from "../components/OnboardingChecklist";
import KitchenReadinessWidget from "../components/KitchenReadinessWidget";
import { fmtRp, fmtNum, fmtUnit, STOCK_STATUS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const MASCOT = `${process.env.PUBLIC_URL || ""}/brand/mascot-chef.png`;
const CART_ILLUS = `${process.env.PUBLIC_URL || ""}/brand/illustration-cart.png`;

const compact = (v) => {
  const n = Number(v || 0);
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(0) + "K";
  return n;
};

const trendPct = (arr) => {
  if (!arr || arr.length < 2) return null;
  const a = Number(arr[arr.length - 2]?.value || 0);
  const b = Number(arr[arr.length - 1]?.value || 0);
  if (a === 0) return b > 0 ? 100 : null;
  return Math.round(((b - a) / a) * 100);
};

const HEALTH_TONE = { EXCELLENT: "success", GOOD: "info", WARNING: "warn", CRITICAL: "danger" };
const HEALTH_TEXT = { EXCELLENT: "text-success-text", GOOD: "text-blue-700", WARNING: "text-warn-text", CRITICAL: "text-danger-text" };
const HEALTH_BAR = { EXCELLENT: "bg-success", GOOD: "bg-blue-500", WARNING: "bg-warn", CRITICAL: "bg-danger" };

function KpiCard({ icon: Icon, label, value, tone = "primary", testid, trend, goodWhenDown }) {
  const tones = {
    primary: "bg-primary-light text-primary",
    success: "bg-success-light text-success-text",
    danger: "bg-danger-light text-danger-text",
    warn: "bg-warn-light text-warn-text",
    info: "bg-blue-100 text-blue-700",
  };
  const up = trend != null && trend >= 0;
  const good = trend == null ? null : goodWhenDown ? !up : up;
  return (
    <Card className="p-4 transition-all hover:-translate-y-1 hover:shadow-cardhover sm:p-5" data-testid={testid}>
      <div className={`mb-3 flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <Icon size={22} />
      </div>
      <p className="text-sm font-semibold text-ink-soft">{label}</p>
      <p className="mt-1 font-heading text-xl font-extrabold text-ink sm:text-2xl">{value}</p>
      {trend != null && (
        <p className={`mt-1 inline-flex items-center gap-0.5 text-[11px] font-bold ${good ? "text-emerald-600" : "text-red-500"}`}>
          {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{Math.abs(trend)}%
        </p>
      )}
    </Card>
  );
}

function HealthStat({ label, value, tone, testid }) {
  const tones = {
    success: "bg-success-light text-success-text",
    warn: "bg-warn-light text-warn-text",
    danger: "bg-danger-light text-danger-text",
  };
  return (
    <div className={`rounded-2xl px-3 py-3 text-center ${tones[tone]}`} data-testid={testid}>
      <p className="font-heading text-2xl font-extrabold leading-none">{value}</p>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-wider">{label}</p>
    </div>
  );
}

function AlertTile({ icon: Icon, label, value, tone, onClick, testid }) {
  const tones = {
    danger: "bg-danger-light text-danger-text",
    warn: "bg-warn-light text-warn-text",
    info: "bg-blue-100 text-blue-700",
    primary: "bg-primary-light text-primary",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-cardhover active:scale-95">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${tones[tone]}`}><Icon size={20} /></div>
      <div className="min-w-0">
        <p className="font-heading text-2xl font-extrabold leading-none text-ink">{value}</p>
        <p className="mt-1 text-xs font-semibold text-ink-soft">{label}</p>
      </div>
    </button>
  );
}

function BranchChip({ label, address, logo, active, onClick, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex w-40 shrink-0 snap-start flex-col items-start gap-2 rounded-2xl border-2 p-3 text-left transition-all active:scale-95 ${active ? "border-primary bg-orange-50 shadow-card" : "border-line bg-white hover:border-primary/40"}`}>
      <div className={`flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl ${active ? "bg-primary text-white" : "bg-app text-primary"}`}>
        {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : <Icon size={22} />}
      </div>
      <div className="w-full">
        <p className="truncate font-heading text-sm font-extrabold text-ink">{label}</p>
        <p className="truncate text-[11px] text-ink-mute">{address}</p>
      </div>
    </button>
  );
}

function MiniCard({ icon: Icon, label, value, gradient, testid }) {
  return (
    <div className={`rounded-2xl p-4 text-white shadow-card ${gradient}`} data-testid={testid}>
      <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-white/25 backdrop-blur"><Icon size={18} /></div>
      <p className="text-xs font-semibold text-white/90">{label}</p>
      <p className="font-heading text-lg font-extrabold leading-tight">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user, isRole } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [branchMap, setBranchMap] = useState({});
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [support, setSupport] = useState({ phone: "", company: "Central Flow" });
  const [drafting, setDrafting] = useState(false);
  const [interval, setIntervalDays] = useState("");
  const [savingInterval, setSavingInterval] = useState(false);
  const [trendBranch, setTrendBranch] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const canEditSettings = isRole("SUPER_ADMIN", "OWNER");

  const saveInterval = async () => {
    setSavingInterval(true);
    try {
      await api.put("/settings", { stock_take_interval_days: Number(interval) });
      toast.success(t("Stock-take interval updated"));
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSavingInterval(false); }
  };

  const createDraft = async () => {
    setDrafting(true);
    try {
      const { data: res } = await api.post("/purchase-orders/from-reorder", {});
      toast.success(`${t("Created")} ${res.created} ${t("draft purchase order(s)")}`);
      navigate("/procurement");
    } catch (e) { toast.error(e.response?.data?.detail || t("Nothing to reorder")); }
    finally { setDrafting(false); }
  };

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => {
      setData(data);
      setIntervalDays(String(data.inventory_control?.stock_take_interval_days || 7));
      const first = (data.branch_health_scores || [])[0];
      if (first) setTrendBranch((tb) => tb || first.branch_id);
    }).catch(() => {});
    api.get("/branches").then(({ data }) => {
      const m = {};
      data.forEach((b) => { m[b.id] = b; });
      setBranchMap(m);
      setBranches(data);
    }).catch(() => {});
    api.get("/analytics/purchasing").then(({ data }) => setAnalytics(data)).catch(() => setAnalytics(null));
    api.get("/settings").then(({ data }) => setSupport({
      phone: data.company_profile?.phone || "",
      company: data.company_profile?.company_name || "Central Flow",
    })).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!trendBranch) return;
    api.get(`/branch/snapshots?branch_id=${trendBranch}`).then(({ data }) => setSnapshots(data)).catch(() => setSnapshots([]));
  }, [trendBranch]);

  if (!data) return <PageLoader />;
  const k = data.kpis;
  const ch = data.central_inventory_health || { safe: 0, low: 0, critical: 0, total: 0 };
  const bt = data.branch_inventory_totals || { safe: 0, low: 0, critical: 0, total: 0 };
  const ac = data.alert_center || { low_stock: 0, critical_stock: 0, pending_approval: 0, pending_opname: 0 };
  const ex = data.expiry_alert || { expired: 0, expiring_7: 0, expiring_30: 0 };
  const ic = data.inventory_control || {};
  const ck = data.cost_kpis || {};
  const canDraft = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");

  // ----- branch scope (client-side, no backend change) -----
  const selBranch = selectedBranchId ? branchMap[selectedBranchId] : null;
  const bHealth = selectedBranchId ? (data.branch_health_scores || []).find((h) => h.branch_id === selectedBranchId) : null;
  const bInv = selectedBranchId ? (data.branch_inventory_health || []).find((b) => b.branch_id === selectedBranchId) : { safe: 0, low: 0, critical: 0 };
  const bPerf = selBranch ? (data.branch_performance || []).find((p) => p.branch === selBranch.name) : null;
  const bSubmit = selectedBranchId ? (data.branch_status || []).find((s) => s.branch_id === selectedBranchId) : null;
  const lowest = selectedBranchId ? (data.lowest_stock_items || []).filter((r) => r.branch_id === selectedBranchId) : (data.lowest_stock_items || []);
  const reorder = selectedBranchId ? (data.reorder_suggestions || []).filter((r) => r.branch_id === selectedBranchId) : (data.reorder_suggestions || []);
  const submittedCount = (data.branch_status || []).filter((s) => s.submitted).length;
  const totalBranches = (data.branch_status || []).length || branches.length;
  const topSupplier = analytics?.supplier_spend?.[0] || null;
  const ordersThisMonth = (data.branch_performance || []).reduce((s, p) => s + (p.orders || 0), 0);
  const purchTrend = trendPct(data.purchasing_trend);
  const wasteTrend = trendPct(data.waste_trend);
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      {/* First-time tenant onboarding (self-hides for existing tenants & once dismissed/complete) */}
      <OnboardingChecklist />

      {/* Operations Control Tower — today's 7-phase operational pipeline (all-branches view) */}
      {!selectedBranchId && (
        <div className="mb-6"><ControlTower embedded /></div>
      )}

      {/* Branch selector */}
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-2">
          <Layers size={16} className="text-primary" />
          <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">{t("Branch Overview")}</h2>
        </div>
        <div className="flex snap-x gap-3 overflow-x-auto pb-1" data-testid="branch-selector">
          <BranchChip label={t("All Branches")} address={`${totalBranches} ${t("branches")}`} active={!selectedBranchId} icon={Building2} onClick={() => setSelectedBranchId(null)} testid="branch-chip-all" />
          {branches.map((b) => (
            <BranchChip key={b.id} label={b.name} address={b.address || t("No address")} logo={b.logo} active={selectedBranchId === b.id} icon={Store} onClick={() => setSelectedBranchId(b.id)} testid={`branch-chip-${b.name}`} />
          ))}
          {canEditSettings && (
            <button onClick={() => navigate("/master-data")} data-testid="branch-chip-add"
              className="flex w-32 shrink-0 snap-start flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-line p-3 text-ink-mute transition-all hover:border-primary hover:text-primary active:scale-95">
              <Plus size={22} /><span className="text-xs font-bold">{t("Add Branch")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Owner — Today's Closing Status per branch (Mini POS) */}
      {isRole("OWNER", "SUPER_ADMIN") && !selectedBranchId && (
        <div className="mb-6"><ClosingStatusWidget /></div>
      )}

      {/* Cute welcome hero */}
      <div className="relative mb-6 overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-amber-50 to-white p-5 shadow-card" data-testid="owner-hero">
        <div className="relative z-10 max-w-[66%]">
          <p className="font-heading text-xl font-extrabold text-ink sm:text-2xl" data-testid="hero-title">
            {selectedBranchId ? selBranch?.name : `${t("Welcome")}, ${firstName}!`}
          </p>
          <p className="mt-1 text-sm text-ink-soft">
            {selectedBranchId ? `${t("Here's today's summary for")} ${selBranch?.name}.` : t("Here's today's operational summary across all branches.")}
          </p>
          {selectedBranchId && bHealth && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-sm font-bold shadow-sm" data-testid="branch-health-pill">
              <ShieldCheck size={15} className="text-primary" /> {t("Health Score")}: {bHealth.score} · {t(bHealth.label)}
            </div>
          )}
        </div>
        <img src={MASCOT} alt="" className="pointer-events-none absolute -bottom-2 right-2 h-28 w-28 object-contain drop-shadow-md sm:h-36 sm:w-36" />
        <img src={CART_ILLUS} alt="" className="pointer-events-none absolute bottom-2 right-28 hidden h-24 w-24 object-contain drop-shadow-sm md:block" />
      </div>

      {/* KPIs (scoped) */}
      {!selectedBranchId ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5" data-testid="kpi-row">
          <KpiCard icon={Wallet} label={t("Today's Purchases")} value={fmtRp(k.todays_purchases)} tone="primary" trend={purchTrend} testid="kpi-purchases" />
          <KpiCard icon={Trash2} label={t("Monthly Waste")} value={fmtRp(k.monthly_waste)} tone="danger" trend={wasteTrend} goodWhenDown testid="kpi-waste" />
          <KpiCard icon={Boxes} label={t("Stock Value")} value={fmtRp(k.stock_value)} tone="success" testid="kpi-stock-value" />
          <KpiCard icon={AlertTriangle} label={t("Critical Items")} value={k.critical_items} tone="danger" testid="kpi-critical" />
          <KpiCard icon={AlertTriangle} label={t("Low Items")} value={k.low_items} tone="warn" testid="kpi-low" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5" data-testid="kpi-row-branch">
          <KpiCard icon={AlertTriangle} label={t("Critical Items")} value={bInv.critical} tone="danger" testid="bkpi-critical" />
          <KpiCard icon={AlertTriangle} label={t("Low Items")} value={bInv.low} tone="warn" testid="bkpi-low" />
          <KpiCard icon={Gauge} label={t("Inventory Accuracy")} value={bHealth?.accuracy_pct != null ? `${bHealth.accuracy_pct}%` : "—"} tone="info" testid="bkpi-accuracy" />
          <KpiCard icon={Percent} label={t("Waste %")} value={`${bHealth?.waste_pct ?? 0}%`} tone="danger" testid="bkpi-waste" />
          <KpiCard icon={ShoppingBag} label={t("Orders This Month")} value={bPerf?.orders ?? 0} tone="primary" testid="bkpi-orders" />
        </div>
      )}

      {/* Cost & Procurement KPIs (all-branches only) */}
      {!selectedBranchId && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5" data-testid="cost-kpi-row">
          <KpiCard icon={Warehouse} label={t("Warehouse Inventory Value")} value={fmtRp(ck.warehouse_inventory_value)} tone="success" testid="ckpi-warehouse-value" />
          <KpiCard icon={Coins} label={t("Total Inventory Value")} value={fmtRp(ck.total_inventory_value)} tone="primary" testid="ckpi-total-value" />
          <KpiCard icon={Wallet} label={t("Purchases Today")} value={fmtRp(ck.purchases_today)} tone="info" testid="ckpi-purchases-today" />
          <KpiCard icon={ShoppingBag} label={t("Purchases This Month")} value={fmtRp(ck.purchases_month)} tone="info" testid="ckpi-purchases-month" />
          <KpiCard icon={Truck} label={t("Sent To Branches")} value={fmtRp(ck.sent_to_branches_month)} tone="warn" testid="ckpi-sent-branches" />
        </div>
      )}

      {/* Cost Insights widgets (all-branches only) */}
      {!selectedBranchId && <CostInsights />}

      {/* Production Insights (Owner / Super Admin only, all-branches) */}
      {!selectedBranchId && isRole("OWNER", "SUPER_ADMIN") && <ProductionInsights />}

      {/* Margin Dashboard summary (Owner / Super Admin only, all-branches) */}
      {!selectedBranchId && isRole("OWNER", "SUPER_ADMIN") && <MarginInsights />}

      {/* Kitchen Readiness by Branch (Phase 2A — Owner/HQ only, all-branches view) */}
      {!selectedBranchId && isRole("OWNER", "SUPER_ADMIN") && <div className="mt-6"><KitchenReadinessWidget /></div>}

      {/* Employee Meal (Konsumsi Karyawan) — company-wide or selected branch */}
      <div className="mt-8"><EmployeeMealWidget branchId={selectedBranchId} /></div>

      {/* Alert tiles (scoped) */}
      {!selectedBranchId ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="alert-center">
            <AlertTile icon={AlertTriangle} label={t("Critical Stock")} value={ac.critical_stock} tone="danger" onClick={() => navigate("/inventory")} testid="alert-critical" />
            <AlertTile icon={Bell} label={t("Low Stock")} value={ac.low_stock} tone="warn" onClick={() => navigate("/inventory")} testid="alert-low" />
            <AlertTile icon={ShieldCheck} label={t("Pending Approval")} value={ac.pending_approval} tone="info" onClick={() => navigate("/approvals")} testid="alert-approval" />
            <AlertTile icon={ClipboardCheck} label={t("Pending Opname")} value={ac.pending_opname} tone="primary" onClick={() => navigate("/opname")} testid="alert-opname" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4" data-testid="expiry-alert-row">
            <AlertTile icon={CalendarClock} label={t("Expired")} value={ex.expired} tone="danger" onClick={() => navigate("/expiry")} testid="expiry-tile-expired" />
            <AlertTile icon={CalendarClock} label={t("Expiring ≤7 Days")} value={ex.expiring_7} tone="danger" onClick={() => navigate("/expiry")} testid="expiry-tile-7" />
            <AlertTile icon={CalendarClock} label={t("Expiring ≤30 Days")} value={ex.expiring_30} tone="warn" onClick={() => navigate("/expiry")} testid="expiry-tile-30" />
          </div>
        </>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-testid="alert-center-branch">
          <AlertTile icon={AlertTriangle} label={t("Critical Stock")} value={bInv.critical} tone="danger" onClick={() => navigate("/inventory")} testid="balert-critical" />
          <AlertTile icon={Bell} label={t("Low Stock")} value={bInv.low} tone="warn" onClick={() => navigate("/inventory")} testid="balert-low" />
          <AlertTile icon={PackageCheck} label={t("Pending Receiving")} value={bHealth?.outstanding_receiving ?? 0} tone="info" onClick={() => navigate("/inventory")} testid="balert-receiving" />
          <AlertTile icon={CalendarClock} label={t("Expired")} value={bHealth?.expired_items ?? 0} tone="warn" onClick={() => navigate("/expiry")} testid="balert-expired" />
        </div>
      )}

      {/* Branch inventory health (branch view) */}
      {selectedBranchId && (
        <Card className="mt-6 p-5" data-testid="branch-inv-health-card">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold">
            <Store size={18} className="text-primary" /> {t("Inventory Health")} · {selBranch?.name}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <HealthStat label={t("Safe")} value={bInv.safe} tone="success" testid="binv-safe" />
            <HealthStat label={t("Low")} value={bInv.low} tone="warn" testid="binv-low" />
            <HealthStat label={t("Critical")} value={bInv.critical} tone="danger" testid="binv-critical" />
          </div>
          {bHealth && (
            <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-ink-soft sm:grid-cols-3">
              <span>{t("Accuracy")}: <b className="text-ink">{bHealth.accuracy_pct != null ? `${bHealth.accuracy_pct}%` : "—"}</b></span>
              <span>{t("Waste")}: <b className="text-ink">{bHealth.waste_pct}%</b></span>
              <span>{t("Receiving")}: <b className="text-ink">{bHealth.outstanding_receiving}</b></span>
              <span>{t("Expired")}: <b className="text-ink">{bHealth.expired_items}</b></span>
              <span>{t("Last count")}: <b className="text-ink">{bHealth.last_stock_take ? new Date(bHealth.last_stock_take).toLocaleDateString() : t("Never")}</b></span>
              <span>{t("Today")}: <b className="text-ink">{bSubmit?.submitted ? t("Submitted today") : t("Not yet ordered today")}</b></span>
            </div>
          )}
        </Card>
      )}

      {/* ===== Company-wide sections (All Branches view only) ===== */}
      {!selectedBranchId && (<>
      {/* ---------------- Inventory Cockpit ---------------- */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="central-health-card">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold">
            <Warehouse size={18} className="text-primary" /> {t("Central Warehouse Health")}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <HealthStat label={t("Safe")} value={ch.safe} tone="success" testid="central-safe" />
            <HealthStat label={t("Low")} value={ch.low} tone="warn" testid="central-low" />
            <HealthStat label={t("Critical")} value={ch.critical} tone="danger" testid="central-critical" />
          </div>
          <p className="mt-3 text-xs text-ink-mute">{ch.total} {t("tracked products in central warehouse")}</p>
        </Card>

        <Card className="p-5" data-testid="branch-health-card">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold">
            <Store size={18} className="text-primary" /> {t("Branch Inventory Health")}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <HealthStat label={t("Safe")} value={bt.safe} tone="success" testid="branch-safe" />
            <HealthStat label={t("Low")} value={bt.low} tone="warn" testid="branch-low" />
            <HealthStat label={t("Critical")} value={bt.critical} tone="danger" testid="branch-critical" />
          </div>
          <p className="mt-3 text-xs text-ink-mute">{t("Across")} {(data.branch_inventory_health || []).length} {t("branches")} · {bt.total} {t("records")}</p>
        </Card>
      </div>

      {/* ---------------- Inventory Control (Step 1) ---------------- */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="inventory-control-kpis">
        <KpiCard icon={Gauge} label={t("Inventory Accuracy")} value={ic.avg_inventory_accuracy != null ? `${ic.avg_inventory_accuracy}%` : "—"} tone="info" testid="kpi-accuracy" />
        <KpiCard icon={Percent} label={t("Waste %")} value={`${ic.company_waste_pct ?? 0}%`} tone="danger" testid="kpi-waste-pct" />
        <KpiCard icon={TrendingUp} label={t("Variance %")} value={ic.variance_pct != null ? `${ic.variance_pct}%` : "—"} tone="warn" testid="kpi-variance" />
        <KpiCard icon={PackageCheck} label={t("Pending Receiving")} value={ic.pending_receiving ?? 0} tone="primary" testid="kpi-pending-receiving" />
      </div>

      <Card className="mt-6 overflow-hidden p-0" data-testid="branch-health-scores">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-4">
          <Gauge size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Branch Inventory Health Score")}</h3>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {ic.overdue_stock_takes > 0 && <Badge tone="danger"><CalendarClock size={12} /> {ic.overdue_stock_takes} {t("overdue")}</Badge>}
            {canEditSettings && (
              <div className="flex items-center gap-1.5">
                <span className="hidden text-xs text-ink-mute sm:block">{t("Stock take every")}</span>
                <input type="number" value={interval} onChange={(e) => setIntervalDays(e.target.value)} className="w-14 rounded-lg border border-line px-2 py-1 text-sm" data-testid="stock-take-interval-input" />
                <span className="text-xs text-ink-mute">{t("days")}</span>
                <Button size="sm" variant="outline" onClick={saveInterval} loading={savingInterval} data-testid="save-interval-button">{t("Save")}</Button>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
          {(data.branch_health_scores || []).map((h) => (
            <div key={h.branch_id} className="rounded-2xl border border-line p-4" data-testid={`branch-score-${h.branch_name}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <BranchBadge branch={branchMap[h.branch_id]} />
                  <span className="truncate font-heading font-bold text-ink">{h.branch_name}</span>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {h.stock_take_overdue && <Badge tone="danger" data-testid={`overdue-${h.branch_name}`}>{t("Overdue")}</Badge>}
                  <Badge tone={HEALTH_TONE[h.label]}>{t(h.label)}</Badge>
                </div>
              </div>
              <div className="mt-3 flex items-end gap-3">
                <span className={`font-heading text-3xl font-extrabold leading-none ${HEALTH_TEXT[h.label]}`}>{h.score}</span>
                <div className="mb-1 flex-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-stone-100">
                    <div className={`h-full rounded-full ${HEALTH_BAR[h.label]}`} style={{ width: `${h.score}%` }} />
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-ink-soft">
                <span>{t("Accuracy")}: <b className="text-ink">{h.accuracy_pct != null ? `${h.accuracy_pct}%` : "—"}</b></span>
                <span>{t("Waste")}: <b className="text-ink">{h.waste_pct}%</b></span>
                <span>{t("Critical")}: <b className="text-ink">{h.critical_count}</b></span>
                <span>{t("Receiving")}: <b className="text-ink">{h.outstanding_receiving}</b></span>
                <span>{t("Expired")}: <b className="text-ink">{h.expired_items}</b></span>
                <span>{t("Last count")}: <b className="text-ink">{h.last_stock_take ? new Date(h.last_stock_take).toLocaleDateString() : t("Never")}</b></span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mt-6 overflow-hidden p-0" data-testid="accuracy-ranking">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <Award size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Branch Accuracy Ranking")}</h3>
          <span className="ml-auto text-xs text-ink-mute">{t("Latest stock take")}</span>
        </div>
        <div className="divide-y divide-line">
          {(data.accuracy_ranking || []).map((r, idx) => (
            <div key={r.branch_id} className="flex items-center gap-3 px-5 py-3" data-testid={`accuracy-rank-${idx}`}>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-100 text-sm font-bold text-ink-soft">{idx + 1}</span>
              <span className="min-w-0 flex-1 truncate font-semibold text-ink">{r.branch_name}</span>
              <span className="hidden text-xs text-ink-mute sm:block">{r.last_stock_take ? new Date(r.last_stock_take).toLocaleDateString() : t("No stock take")}</span>
              <Badge tone={r.accuracy_pct == null ? "mute" : r.accuracy_pct >= 95 ? "success" : r.accuracy_pct >= 80 ? "warn" : "danger"}>{r.accuracy_pct != null ? `${r.accuracy_pct}%` : "—"}</Badge>
            </div>
          ))}
        </div>
      </Card>

      {/* Branch Performance Trends (Inventory Snapshot History) */}
      <Card className="mt-6 p-5" data-testid="branch-trends">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Activity size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Branch Performance Trends")}</h3>
          <Select value={trendBranch} onChange={(e) => setTrendBranch(e.target.value)} className="ml-auto w-48" data-testid="trend-branch-select">
            {(data.branch_health_scores || []).map((h) => <option key={h.branch_id} value={h.branch_id}>{h.branch_name}</option>)}
          </Select>
        </div>
        {snapshots.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-mute" data-testid="trends-empty">{t("No stock-take history yet for this branch. Trends appear after each completed stock take.")}</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={snapshots.map((s) => ({ date: (s.date || "").slice(5, 10), Accuracy: s.accuracy_pct, Health: s.health_score, "Waste %": s.waste_pct }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis fontSize={11} domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Accuracy" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Health" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Waste %" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="trends-table">
                <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <th className="py-2">{t("Date")}</th><th className="py-2 text-right">{t("Accuracy")}</th><th className="py-2 text-right">{t("Variance")}</th>
                  <th className="py-2 text-right">{t("Waste %")}</th><th className="py-2 text-right">{t("Stock Value")}</th><th className="py-2 text-center">{t("Health")}</th></tr></thead>
                <tbody>
                  {[...snapshots].reverse().slice(0, 8).map((s) => (
                    <tr key={s.id} className="border-b border-line/60">
                      <td className="py-2">{(s.date || "").slice(0, 10)}</td>
                      <td className="py-2 text-right">{s.accuracy_pct != null ? `${s.accuracy_pct}%` : "—"}</td>
                      <td className="py-2 text-right">{s.variance_pct != null ? `${s.variance_pct}%` : "—"}</td>
                      <td className="py-2 text-right">{s.waste_pct ?? 0}%</td>
                      <td className="py-2 text-right">{fmtRp(s.stock_value)}</td>
                      <td className="py-2 text-center"><Badge tone={HEALTH_TONE[s.health_label]}>{s.health_score}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
      </>)}

      {/* Top 10 Lowest Stock Items (scoped) */}
      <Card className="mt-6 overflow-hidden p-0" data-testid="lowest-stock-card">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <AlertTriangle size={18} className="text-danger" />
          <h3 className="font-heading text-lg font-bold">{t("Top 10 Lowest Stock Items")}</h3>
          <span className="ml-auto hidden text-xs text-ink-mute sm:inline">{selectedBranchId ? selBranch?.name : t("Central + all branches · most urgent first")}</span>
        </div>
        {lowest.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink-mute">{t("All stock levels are healthy.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-5 py-3">{t("Product")}</th>
                <th className="px-5 py-3">{t("Location")}</th>
                <th className="px-5 py-3 text-right">{t("Current / Min")}</th>
                <th className="px-5 py-3 text-center">{t("Status")}</th>
              </tr></thead>
              <tbody>
                {lowest.map((r, idx) => {
                  const st = STOCK_STATUS[r.status] || { label: r.status, tone: "mute" };
                  return (
                    <tr key={`${r.scope}-${r.branch_id || "c"}-${r.product_id}`} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`lowest-row-${idx}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <ProductAvatar image={r.image} name={r.product_name} size="sm" />
                          <span className="font-semibold text-ink">{r.product_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-ink-soft">
                          {r.scope === "central" ? <Warehouse size={14} /> : <MapPin size={14} />}
                          {r.location}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold">
                        {fmtNum(r.current_stock)} / {fmtNum(r.minimum_stock)} <span className="text-[11px] font-bold uppercase text-ink-mute">{fmtUnit(r.unit)}</span>
                      </td>
                      <td className="px-5 py-3 text-center"><Badge tone={st.tone}>{t(st.label)}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Reorder Suggestions */}
      <Card className="mt-6 overflow-hidden p-0" data-testid="reorder-card">
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <ShoppingCart size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Reorder Suggestions")}</h3>
          <span className="ml-auto hidden text-xs text-ink-mute sm:inline">{t("Items at or below minimum · suggested top-up to ideal")}</span>
          {canDraft && reorder.length > 0 && (
            <button onClick={createDraft} disabled={drafting} data-testid="create-purchase-draft"
              className="ml-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-primary-hover disabled:opacity-50">
              <ShoppingCart size={14} /> {drafting ? t("Creating...") : t("Create Purchase Draft")}
            </button>
          )}
        </div>
        {reorder.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink-mute">{t("No reorders needed — all stock above minimum.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-5 py-3">{t("Product")}</th><th className="px-5 py-3">{t("Location")}</th>
                <th className="px-5 py-3 text-right">{t("Current / Ideal")}</th><th className="px-5 py-3 text-right">{t("Suggested Order")}</th>
                <th className="px-5 py-3 text-center">{t("Status")}</th></tr></thead>
              <tbody>
                {reorder.map((r, idx) => {
                  const st = STOCK_STATUS[r.status] || { label: r.status, tone: "mute" };
                  return (
                    <tr key={`${r.scope}-${r.branch_id || "c"}-${r.product_id}`} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`reorder-row-${idx}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <ProductAvatar image={r.image} name={r.product_name} size="sm" />
                          <span className="font-semibold text-ink">{r.product_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-ink-soft">
                          {r.scope === "central" ? <Warehouse size={14} /> : <MapPin size={14} />}{r.location}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-ink-soft">{fmtNum(r.current_stock)} / {fmtNum(r.ideal_stock)} <span className="text-[11px] font-bold uppercase text-ink-mute">{fmtUnit(r.unit)}</span></td>
                      <td className="px-5 py-3 text-right font-bold text-primary">+{fmtNum(r.suggested_qty)} <span className="text-[11px] font-bold uppercase text-ink-mute">{fmtUnit(r.unit)}</span></td>
                      <td className="px-5 py-3 text-center"><Badge tone={st.tone}>{t(st.label)}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ===== Company-wide breakdown & charts (All Branches view only) ===== */}
      {!selectedBranchId && (<>
      {/* Per-branch inventory health breakdown */}
      <Card className="mt-6 p-5" data-testid="branch-health-breakdown">
        <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold">
          <MapPin size={18} className="text-primary" /> {t("Stock Health by Branch")}
        </h3>
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          {(data.branch_inventory_health || []).map((b) => (
            <div key={b.branch_id} className="flex items-center justify-between rounded-xl border border-line px-4 py-2.5">
              <BranchBadge branch={branchMap[b.branch_id] || { name: b.branch_name }} size="sm" />
              <div className="flex gap-1.5">
                <Badge tone="success">{b.safe} {t("Safe")}</Badge>
                <Badge tone="warn">{b.low} {t("Low")}</Badge>
                <Badge tone="danger">{b.critical} {t("Critical")}</Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 font-heading text-lg font-bold">{t("Purchasing Trend (7 days)")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.purchasing_trend}>
              <defs>
                <linearGradient id="cp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0efed" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} width={48} tickFormatter={compact} />
              <Tooltip formatter={(v) => fmtRp(v)} />
              <Area type="monotone" dataKey="value" stroke="#F97316" strokeWidth={2.5} fill="url(#cp)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-lg font-bold">{t("Waste Trend (7 days)")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.waste_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0efed" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} width={48} tickFormatter={compact} />
              <Tooltip formatter={(v) => fmtRp(v)} />
              <Bar dataKey="value" fill="#EF4444" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold">
            <Store size={18} className="text-primary" /> {t("Branch Submission Status (Today)")}
          </h3>
          <div className="space-y-2.5" data-testid="branch-status-list">
            {data.branch_status.map((b) => (
              <div key={b.branch_id} className="flex items-center justify-between rounded-xl border border-line px-4 py-2.5 transition-colors hover:bg-stone-50">
                <BranchBadge branch={branchMap[b.branch_id] || { name: b.branch_name }} size="sm" />
                <div className="flex gap-2">
                  <Badge tone={b.submitted ? "success" : "mute"}>{b.submitted ? t("Order Submitted") : t("No Order")}</Badge>
                  <Badge tone={b.closing_completed ? "success" : "warn"}>{b.closing_completed ? t("Closed") : t("Open")}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-lg font-bold">{t("Branch Performance (Orders this month)")}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.branch_performance} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0efed" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="branch" tick={{ fontSize: 12, fill: "#57534E" }} axisLine={false} tickLine={false} width={90} />
              <Tooltip />
              <Bar dataKey="orders" fill="#F97316" radius={[0, 6, 6, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      </>)}

      {/* Bottom cute cards */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="owner-mini-cards">
        <MiniCard icon={Wallet} label={t("Today's Purchases")} value={fmtRp(k.todays_purchases)} gradient="bg-gradient-to-br from-orange-400 to-primary" testid="mini-purchases" />
        <MiniCard icon={ShoppingBag} label={t("Orders This Month")} value={ordersThisMonth} gradient="bg-gradient-to-br from-blue-400 to-blue-600" testid="mini-orders" />
        <MiniCard icon={Star} label={t("Top Supplier")} value={topSupplier ? topSupplier.supplier_name : "—"} gradient="bg-gradient-to-br from-amber-400 to-amber-500" testid="mini-supplier" />
        <MiniCard icon={BadgeCheck} label={t("Active Today")} value={`${submittedCount}/${totalBranches}`} gradient="bg-gradient-to-br from-emerald-400 to-emerald-600" testid="mini-active" />
      </div>

      {/* Help card */}
      <div className="mt-4 flex items-center gap-4 overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5" data-testid="help-card">
        <img src={MASCOT} alt="" className="h-16 w-16 shrink-0 object-contain" />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-base font-extrabold text-ink">{t("Need Help?")}</p>
          <p className="text-sm text-ink-soft">{t("Contact the Central Flow team!")}</p>
        </div>
        {support.phone ? (
          <a href={`https://wa.me/${support.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="help-whatsapp"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-bold text-white transition-all hover:bg-emerald-600 active:scale-95">
            <MessageCircle size={16} /> <span className="hidden sm:inline">{t("Chat via WhatsApp")}</span>
          </a>
        ) : (
          <button onClick={() => navigate("/notifications")} data-testid="help-notifications"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-bold text-white transition-all hover:bg-emerald-600 active:scale-95">
            <Bell size={16} /> <span className="hidden sm:inline">{t("Notifications")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
